import React, { useMemo } from "react";
import ReactModal from "react-modal";
import cn from "clsx";
import "./Modal.css";

export interface ModalProps {
  /**
   * Boolean to show/hide the modal
   *
   * @default false
   */
  isOpen?: boolean;
  /**
   * Boolean to enable/disable closing the modal.
   *
   * This helps when you want a dialog to be explicitly interacted with
   * and avoid closing by pressing esc or clicking the overlay.
   *
   * @default false
   */
  allowClose?: boolean;
  /**
   * Custom class name to give to the modal.
   *
   * The class name is used for all the various states as well.
   *
   * @default "modal"
   */
  className?: string;
  /**
   * Custom class name to give to the modal overlay.
   *
   * The overlay class name is used for all the various states as well.
   *
   * @default "modal-overlay"
   */
  overlayClassName?: string;
  /**
   * Custom class name to give to the body when the modal is open.
   *
   * @default "modal-is-open"
   */
  bodyOpenClassName?: string;
  /**
   * Event handler to fire when the modal is closed.
   */
  onClose?: (
    event: MouseEvent | React.MouseEvent | KeyboardEvent | React.KeyboardEvent
  ) => void;
  /**
   * The content to display within the modal.
   */
  children: React.ReactNode | React.ReactNodeArray;
}

// This suppresses ReactModal error messages in the console and should help
// with accessibility
const ROOT_NODE: HTMLElement | null = document.getElementById("root");
if (ROOT_NODE) {
  ReactModal.setAppElement(ROOT_NODE);
}

export default function Modal({
  isOpen = false,
  allowClose = true,
  className,
  overlayClassName = "modal-overlay",
  bodyOpenClassName = "modal-is-open",
  onClose,
  children
}: ModalProps) {
  const classNames = useMemo(
    () => ({
      base: cn("modal", className),
      afterOpen: `modal-state-after-open`,
      beforeClose: `modal-state-before-close`
    }),
    [className]
  );

  const overlayClassNames = useMemo(
    () => ({
      base: `${overlayClassName}`,
      afterOpen: `${overlayClassName}-state-after-open`,
      beforeClose: `${overlayClassName}-state-before-close`
    }),
    [overlayClassName]
  );

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnEsc={!allowClose}
      shouldCloseOnOverlayClick={!allowClose}
      className={classNames}
      overlayClassName={overlayClassNames}
      bodyOpenClassName={bodyOpenClassName}
    >
      {children}
    </ReactModal>
  );
}
